/**
 * Activity log requests (PRD §5.8).
 *
 * In `lib/` rather than a feature's `api/` folder: the timeline is shared
 * domain UI used by several features, and only a feature's own `api/` layer may
 * be reached from that feature (plan §3.1).
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.8, plan §3.1
 */
import type {
  Activity,
  CreateActivityRequest,
  ListActivitiesQuery,
  UpdateActivityRequest,
} from '@nexus/contracts';

import { api } from './api-client';
import type { Paginated } from './pagination';

export type ActivityPage = Paginated<Activity>;

export async function listActivities(query: ListActivitiesQuery): Promise<ActivityPage> {
  return api.get<ActivityPage>('/activities', {
    query: query,
  });
}

export async function createActivity(body: CreateActivityRequest): Promise<Activity> {
  return api.post<Activity>('/activities', body);
}

export async function updateActivity(
  id: string,
  body: UpdateActivityRequest,
): Promise<Activity> {
  return api.patch<Activity>(`/activities/${id}`, body);
}

/**
 * Soft-deletes an activity.
 *
 * `undefined`, not `void`, as the type argument: a 204 carries no body and the
 * client returns undefined for it, and `void` in a value position is not a
 * type the API client can produce.
 */
export async function deleteActivity(id: string): Promise<void> {
  await api.delete<undefined>(`/activities/${id}`);
}
